import React, { FC, useEffect, useState } from "react";

export type TypewriterProps = {
  text: string;
  /** ms before typing begins */
  startDelay?: number;
  /** ms per character */
  speed?: number;
  className?: string;
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Types `text` out one character at a time on mount. Used for the "…, a chess
 * puzzle" suffix after the title. Honors prefers-reduced-motion by rendering
 * the full string immediately.
 */
export const Typewriter: FC<TypewriterProps> = ({
  text,
  startDelay = 400,
  speed = 65,
  className,
}) => {
  const reduced = prefersReducedMotion();
  const [count, setCount] = useState(reduced ? text.length : 0);
  const done = count >= text.length;

  useEffect(() => {
    if (reduced) return;
    let i = 0;
    let interval: ReturnType<typeof setInterval>;
    const startTimer = setTimeout(() => {
      interval = setInterval(() => {
        i += 1;
        setCount(i);
        if (i >= text.length) clearInterval(interval);
      }, speed);
    }, startDelay);
    return () => {
      clearTimeout(startTimer);
      clearInterval(interval);
    };
  }, [text, speed, startDelay, reduced]);

  return (
    <span className={className}>
      {text.slice(0, count)}
      {!done && <span className="typewriter-cursor" aria-hidden="true">▌</span>}
    </span>
  );
};
